import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateVenueProfile, useVenueProfile } from "@/features/venues/model/useVenueProfile";

const schema = z.object({
  business_name: z.string().min(1, "Business name is required").max(200),
  address: z.string().min(1, "Address is required").max(500),
  contact_first_name: z.string().trim().min(1, "Required").max(100),
  contact_last_name: z.string().trim().min(1, "Required").max(100),
  contact_role: z.string().trim().max(100).optional(),
  contact_phone: z.string().max(20).optional(),
  notes: z.string().max(1000).optional(),
});

type FormValues = z.infer<typeof schema>;

export function VenueProfileView() {
  const { data: profile, isLoading, error } = useVenueProfile();
  const update = useUpdateVenueProfile();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      business_name: "",
      address: "",
      contact_first_name: "",
      contact_last_name: "",
      contact_role: "",
      contact_phone: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (profile) {
      reset({
        business_name: profile.business_name,
        address: profile.address,
        contact_first_name: profile.contact_first_name,
        contact_last_name: profile.contact_last_name,
        contact_role: profile.contact_role ?? "",
        contact_phone: profile.contact_phone ?? "",
        notes: profile.notes ?? "",
      });
    }
  }, [
    profile?.id,
    profile?.business_name,
    profile?.address,
    profile?.contact_first_name,
    profile?.contact_last_name,
    profile?.contact_role,
    profile?.contact_phone,
    profile?.notes,
    reset,
  ]);

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (error || !profile) return <div className="text-sm text-destructive">Could not load venue.</div>;

  const onSubmit = handleSubmit((values) =>
    update.mutate({
      business_name: values.business_name,
      address: values.address,
      contact_first_name: values.contact_first_name,
      contact_last_name: values.contact_last_name,
      // Empty string clears server-side; non-empty replaces.
      contact_role: values.contact_role ?? "",
      contact_phone: values.contact_phone || undefined,
      notes: values.notes || undefined,
    }),
  );

  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Your rating</p>
            <p className="mt-1 text-2xl font-semibold">
              {profile.rating_avg != null ? (
                <>
                  ★ {profile.rating_avg.toFixed(1)}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    from completed shifts
                  </span>
                </>
              ) : (
                <span className="text-base font-normal text-muted-foreground">
                  No ratings yet — workers rate you after each completed shift.
                </span>
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold">Venue details</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="business_name" className="text-sm font-medium">
            Business name
          </label>
          <input
            id="business_name"
            {...register("business_name")}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.business_name && <p className="text-xs text-destructive">{errors.business_name.message}</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="address" className="text-sm font-medium">
            Address
          </label>
          <input
            id="address"
            {...register("address")}
            placeholder="123 Main St, Ottawa, ON"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.address && <p className="text-xs text-destructive">{errors.address.message}</p>}
          {profile.address && !profile.geocoded && (
            <div className="mt-2 rounded-md border border-warning bg-warning-light p-3 text-xs">
              <p className="font-medium text-copy">
                We couldn't pin this address on a map.
              </p>
              <p className="mt-1 text-copy-muted">
                Workers won't be able to check in to shifts here until the
                address resolves. Try a clearer format like
                <span className="font-medium"> "123 Main St, Ottawa, ON"</span>
                {" "}or
                <span className="font-medium"> "10 Downing St, London, UK"</span>
                {" "}— include the city and region — then save again.
              </p>
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-md border border-input bg-background p-4">
          <div>
            <p className="text-sm font-medium">Primary contact</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The person workers and our team should ask for. Doesn't have to be the account holder.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="contact_first_name" className="text-sm font-medium">
                First name
              </label>
              <input
                id="contact_first_name"
                {...register("contact_first_name")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {errors.contact_first_name && (
                <p className="text-xs text-destructive">{errors.contact_first_name.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <label htmlFor="contact_last_name" className="text-sm font-medium">
                Last name
              </label>
              <input
                id="contact_last_name"
                {...register("contact_last_name")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {errors.contact_last_name && (
                <p className="text-xs text-destructive">{errors.contact_last_name.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="contact_role" className="text-sm font-medium">
              Role <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="contact_role"
              {...register("contact_role")}
              placeholder="Event Manager · GM · Owner"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="contact_phone" className="text-sm font-medium">
            Contact phone
          </label>
          <input
            id="contact_phone"
            type="tel"
            placeholder="+1 613 555 1000"
            {...register("contact_phone")}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="notes" className="text-sm font-medium">
            Notes <span className="text-muted-foreground">(visible to workers)</span>
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register("notes")}
            placeholder="What kind of venue you are, dress code defaults, anything workers should know."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!isDirty || update.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {update.isPending ? "Saving..." : "Save"}
          </button>
          {update.isSuccess && !isDirty && <span className="text-xs text-muted-foreground">Saved</span>}
        </div>
      </form>
      </section>
    </div>
  );
}
